import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

import { IconComponent } from '../icon.component';

export interface SocialLink {
  type: 'email' | 'phone' | 'linkedin' | 'whatsapp' | 'facebook' | 'twitter' | 'instagram' | 'copyLink';
  value?: string;
  label: string;
  icon: string;
  color?: string;
}

@Component({
  selector: 'app-social-links',
  templateUrl: './social-links.component.html',
  styleUrls: ['./social-links.component.scss'],
  standalone: true,
  imports: [IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SocialLinksComponent {
  @Input() title = 'Neem contact op';
  @Input() subtitle?: string;
  
  // Contact options
  @Input() email?: string;
  @Input() phone?: string;
  @Input() linkedin?: string;
  @Input() whatsapp?: string;
  @Input() personName?: string;
  
  // Share options
  @Input() shareUrl?: string;
  @Input() shareTitle?: string;
  @Input() shareText?: string;
  
  // Control which buttons to show
  @Input() showEmail = true;
  @Input() showPhone = true;
  @Input() showLinkedIn = true;
  @Input() showWhatsApp = true;
  @Input() showFacebook = false;
  @Input() showTwitter = false;
  @Input() showInstagram = false;
  @Input() showCopyLink = false;
  @Input() centered = false;

  get socialLinks(): SocialLink[] {
    const links: SocialLink[] = [];

    if (this.showEmail && this.email) {
      links.push({
        type: 'email',
        value: this.email,
        label: 'Email',
        icon: 'email_filled',
        color: '#6e6e73',
      });
    }

    if (this.showPhone && this.phone) {
      links.push({
        type: 'phone',
        value: this.phone,
        label: 'Telefoon',
        icon: 'call_filled',
        color: '#34C759',
      });
    }

    if (this.showLinkedIn && this.linkedin) {
      links.push({
        type: 'linkedin',
        value: this.linkedin,
        label: 'LinkedIn',
        icon: 'linkedin',
        color: '#0A66C2',
      });
    }

    if (this.showWhatsApp && this.whatsapp) {
      links.push({
        type: 'whatsapp',
        value: this.whatsapp,
        label: 'WhatsApp',
        icon: 'whatsapp',
        color: '#25D366',
      });
    }

    if (this.showFacebook && this.shareUrl) {
      links.push({
        type: 'facebook',
        label: 'Facebook',
        icon: 'facebook',
        color: '#1877F2',
      });
    }

    if (this.showTwitter && this.shareUrl) {
      links.push({
        type: 'twitter',
        label: 'X',
        icon: 'twitter',
        color: '#000000',
      });
    }

    if (this.showInstagram && this.shareUrl) {
      links.push({
        type: 'instagram',
        label: 'Instagram',
        icon: 'instagram',
        color: '#E4405F',
      });
    }

    if (this.showCopyLink && this.shareUrl) {
      links.push({
        type: 'copyLink',
        label: 'Kopieer link',
        icon: 'link',
        color: '#6e6e73',
      });
    }

    return links;
  }

  getActionUrl(link: SocialLink): string {
    const url = this.shareUrl || window.location.href;
    const title = this.shareTitle || '';
    const text = this.shareText || '';

    switch (link.type) {
      case 'email':
        if (this.shareUrl) {
          // Share mode
          const subject = encodeURIComponent(title);
          const body = encodeURIComponent(`${text}\n\n${url}`);
          return `mailto:?subject=${subject}&body=${body}`;
        }
        // Contact mode
        return `mailto:${link.value}`;
      case 'phone':
        return `tel:${link.value}`;
      case 'linkedin':
        if (this.shareUrl) {
          // Share mode
          return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        }
        // Contact mode
        return link.value || '#';
      case 'whatsapp':
        if (this.shareUrl) {
          // Share mode
          const whatsappText = encodeURIComponent(`${title}\n${url}`);
          return `https://wa.me/?text=${whatsappText}`;
        }
        // Contact mode
        return this.getWhatsAppLink(link.value || '');
      case 'facebook':
        return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
      case 'twitter':
        const tweetText = encodeURIComponent(title);
        return `https://twitter.com/intent/tweet?text=${tweetText}&url=${encodeURIComponent(url)}`;
      case 'instagram':
        return `https://instagram.com/blauwtand_it`;
      case 'copyLink':
        return '#';
      default:
        return '#';
    }
  }

  handleClick(event: Event, link: SocialLink): void {
    if (link.type === 'copyLink') {
      event.preventDefault();
      this.copyLink();
    } else if (link.type === 'facebook' || link.type === 'twitter' || link.type === 'instagram' || (link.type === 'linkedin' && this.shareUrl)) {
      event.preventDefault();
      const url = this.getActionUrl(link);
      window.open(url, '_blank');
    }
  }

  getAriaLabel(link: SocialLink): string {
    const name = this.personName || 'contactpersoon';
    
    if (this.shareUrl) {
      // Share mode
      switch (link.type) {
        case 'email': return `Delen via email`;
        case 'linkedin': return `Delen op LinkedIn`;
        case 'facebook': return `Delen op Facebook`;
        case 'twitter': return `Delen op X`;
        case 'instagram': return `Volgen op Instagram`;
        case 'whatsapp': return `Delen via WhatsApp`;
        case 'copyLink': return `Link kopiëren`;
        default: return link.label;
      }
    }
    
    // Contact mode
    switch (link.type) {
      case 'email': return `Email versturen naar ${name}`;
      case 'phone': return `Bellen naar ${name}`;
      case 'linkedin': return `LinkedIn profiel van ${name}`;
      case 'whatsapp': return `WhatsApp met ${name}`;
      default: return link.label;
    }
  }

  isExternalLink(link: SocialLink): boolean {
    if (this.shareUrl) {
      return link.type === 'linkedin' || link.type === 'facebook' || link.type === 'twitter' || link.type === 'instagram' || link.type === 'whatsapp';
    }
    return link.type === 'linkedin' || link.type === 'whatsapp';
  }

  private copyLink(): void {
    const url = this.shareUrl || window.location.href;
    
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        // Could add a toast notification here
      }).catch((err) => {
        console.error('Failed to copy link:', err);
        this.fallbackCopyLink(url);
      });
    } else {
      this.fallbackCopyLink(url);
    }
  }

  private fallbackCopyLink(url: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = url;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
    } catch (err) {
      console.error('Fallback copy failed:', err);
    }
    document.body.removeChild(textArea);
  }

  private getWhatsAppLink(phoneNumber: string): string {
    // Remove all non-digit characters except '+'
    const cleanNumber = phoneNumber.replace(/[^\d+]/g, '');
    return `https://wa.me/${cleanNumber}`;
  }
}
